import React from 'react'
import { Card, Flex, Text } from '@tremor/react'
import { Activity, CheckCircle, Zap, Users } from '@/components/icons'
import type { TeamActivity } from '../types'
import { cn } from '@/lib/utils'

interface ActivityFeedProps {
  activities: TeamActivity[]
}

const activityConfig = {
  booking: {
    icon: CheckCircle,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-100',
    dotColor: 'bg-emerald-500',
  },
  milestone: {
    icon: Zap,
    color: 'text-amber-500',
    bgColor: 'bg-amber-100',
    dotColor: 'bg-amber-500',
  },
  team_update: {
    icon: Users,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100',
    dotColor: 'bg-blue-500',
  },
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'Just now'
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60)
    return `${mins}m ago`
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return `${hours}h ago`
  }
  return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })
}

export function ActivityFeed({ activities }: ActivityFeedProps) {
  return (
    <Card className="border-l-4 border-l-indigo-500 shadow-md hover:shadow-lg p-0 overflow-hidden animate-fade-in transition-shadow duration-200 bg-gradient-to-br from-indigo-50/30 via-white to-white border-t border-r border-b border-indigo-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-100">
        <Flex alignItems="center" className="gap-3">
          <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600">
            <Activity className="w-5 h-5" />
          </div>
          <Text className="text-zinc-950 font-semibold text-lg">
            Team Activity
          </Text>
        </Flex>
      </div>

      {/* Activity List */}
      <div className="px-6 py-4">
        {activities.length > 0 ? (
          <div className="space-y-4">
            {activities.map((activity, index) => {
              const config = activityConfig[activity.activityType]

              return (
                <div key={activity.id} className="flex gap-4">
                  {/* Timeline */}
                  <div className="flex flex-col items-center">
                    <div className={cn(
                      "w-2.5 h-2.5 rounded-full ring-4 ring-white",
                      config.dotColor
                    )} />
                    {index < activities.length - 1 && (
                      <div className="w-px flex-1 bg-zinc-200 mt-2" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-4">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-zinc-700">
                        {activity.operatorName && (
                          <span className="font-semibold text-zinc-900">
                            {activity.operatorName}{' '}
                          </span>
                        )}
                        {activity.description}
                      </p>
                      <span className="text-xs text-zinc-400 whitespace-nowrap">
                        {formatRelativeTime(activity.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="py-8 text-center">
            <Activity className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
            <Text className="text-zinc-400">No recent activity</Text>
          </div>
        )}
      </div>
    </Card>
  )
}
